import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { Tab } from "@headlessui/react";
import ModalIntelliPay from "./modal-intellipay";
import TshirtAndPositionStep from "../../../components/TshirtAndPositionStep";
import CombineBundleOffer from "../../../components/combine-bundle/CombineBundleOffer";
import {
  OPTION_BUNDLE,
  OPTION_SINGLE,
  formatMoney,
  getBundleId,
  getBundlePrice,
  getCombines,
  getDrillCombineCount,
  getTotalSeparatePrice,
  isBundleOfferUsable,
} from "../../../components/combine-bundle/bundleHelpers";
import { useTrackEvent } from "../../../hooks/useTrackEvent";
import { useAxiosInstance } from "../../../services/Interceptor";

import { positions, tshirtSizes } from "../../../utils/PositionStateData";
import {
  getAthleteProfile,
  updateAthleteProfile,
} from "../../../services/AthleteService";
import { getCombineBundleForEvent } from "../../../services/EventsService";
import { toast } from "react-toastify";

export default function RegisterFlowModal({
  open,
  onClose,
  athleteId,
  athleteEmail,
  eventId,
  origin,
  currentPosition,
}) {
  const apiInstance = useAxiosInstance();
  const trackEvent = useTrackEvent();

  const [activeTab, setActiveTab] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [bundleOption, setBundleOption] = useState(OPTION_SINGLE);
  // Handed to ModalIntelliPay; null means the athlete declined (or was never
  // offered) the bundle and pays for the single combine as before.
  const [bundleChoice, setBundleChoice] = useState(null);

  const [tshirtSize, setTshirtSize] = useState("");
  const [position, setPosition] = useState(currentPosition || "");
  const [saving, setSaving] = useState(false);
  // Bumped whenever the modal closes or reopens, so a bundle lookup still in
  // flight can't reopen the upsell or payment step of a flow that was left.
  const flowIdRef = useRef(0);

  useEffect(() => {
    flowIdRef.current += 1;
    setSaving(false);
    if (open) {
      setActiveTab(0);
      setShowPayment(false);
      setBundle(null);
      setBundleOption(OPTION_SINGLE);
      setBundleChoice(null);
      setTshirtSize("");
    }
  }, [open]);

  useEffect(() => {
    async function fetchData() {
      const response = await getAthleteProfile(apiInstance, athleteId);
      setPosition(response.primaryPosition);
    }
    fetchData();
  }, [apiInstance, athleteId]);

  const canContinueToPayment = !!tshirtSize && !!position;

  const saveAndContinueToPayment = async () => {
    if (!canContinueToPayment) return;

    const flowId = flowIdRef.current;
    setSaving(true);
    try {
      let positionUpdated = false;

      if (position) {
        await updateAthleteProfile(apiInstance, athleteId, {
          primaryPosition: position,
        });
        positionUpdated = true;
      }

      if (positionUpdated) {
        toast.success("Position updated successfully");
      }

      // The route answers 204 (empty body) when the combine is in no active
      // bundle or the athlete already owns a combine in it. That and a failed
      // lookup mean no offer, so the athlete goes straight to payment instead
      // of being blocked by an optional upsell.
      const response = await getCombineBundleForEvent(apiInstance, eventId);
      if (flowId !== flowIdRef.current) return;

      const offer = response?.success ? response.data : null;
      if (!isBundleOfferUsable(offer, eventId)) {
        setShowPayment(true);
        return;
      }

      setBundleOption(OPTION_SINGLE);
      setBundle(offer);
      trackEvent("College_ViewedCombineBundleUpsell", {
        event_id: eventId,
        bundle_id: getBundleId(offer),
        bundle_price: getBundlePrice(offer),
        total_separate_price: getTotalSeparatePrice(offer),
        combine_count: getCombines(offer).length,
        combines_with_position_drills: getDrillCombineCount(offer),
        athlete_type: "returning",
        portal_type: "college",
      });
    } catch (err) {
      toast.error("Failed to update registration details. Please try again.");
      throw err;
    } finally {
      if (flowId === flowIdRef.current) setSaving(false);
    }
  };

  const continueFromBundleOffer = () => {
    if (bundleOption === OPTION_BUNDLE) {
      trackEvent("College_AcceptedCombineBundle", {
        event_id: eventId,
        bundle_id: getBundleId(bundle),
        bundle_price: getBundlePrice(bundle),
        athlete_type: "returning",
        portal_type: "college",
      });
      setBundleChoice({
        bundleId: getBundleId(bundle),
        bundlePrice: getBundlePrice(bundle),
        combines: getCombines(bundle),
      });
    } else {
      trackEvent("College_DeclinedCombineBundle", {
        event_id: eventId,
        bundle_id: getBundleId(bundle),
        athlete_type: "returning",
        portal_type: "college",
      });
      setBundleChoice(null);
    }
    setShowPayment(true);
  };

  const showBundleOffer = !!bundle && !showPayment;

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!showPayment) onClose();
      }}
      className="relative z-[250]"
    >
      <DialogBackdrop className="fixed inset-0 bg-black/50" />

      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        {showBundleOffer ? (
          <DialogPanel className="w-full max-w-6xl bg-white rounded-lg shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-gray-900">
                  Combine Bundle
                </div>
                <div className="text-sm text-gray-600">
                  Save on more combines before you pay
                </div>
              </div>

              <button
                type="button"
                className="text-gray-500 hover:bg-gray-100 rounded-full p-2"
                onClick={onClose}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              <CombineBundleOffer
                bundle={bundle}
                eventId={eventId}
                option={bundleOption}
                onOptionChange={setBundleOption}
              />
            </div>

            {/* Footer */}
            <div className="border-t px-5 py-4 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3">
              <button
                type="button"
                onClick={() => setBundle(null)}
                className="px-5 py-2 rounded border"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={continueFromBundleOffer}
                className="px-5 py-2 rounded text-white bg-blue-700 hover:bg-blue-800"
              >
                {bundleOption === OPTION_BUNDLE
                  ? `Upgrade to Bundle – ${formatMoney(getBundlePrice(bundle))}`
                  : "Continue with Single Event"}
              </button>
            </div>
          </DialogPanel>
        ) : !showPayment ? (
          <DialogPanel className="w-full max-w-3xl bg-white rounded-lg shadow-xl overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-gray-900">
                  Complete registration details
                </div>
                <div className="text-sm text-gray-600">
                  Step: {activeTab + 1} of 2
                </div>
              </div>

              <button
                type="button"
                className="text-gray-500 hover:bg-gray-100 rounded-full p-2"
                onClick={onClose}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-5">
              <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
                <Tab.List className="flex gap-2 border-b">
                  {["T-Shirt Size", "Position"].map((label) => (
                    <Tab
                      key={label}
                      className={({ selected }) =>
                        `px-4 py-2 text-sm font-semibold -mb-[1px] border-b-2 ${
                          selected
                            ? "border-blue-600 text-blue-700"
                            : "border-transparent text-gray-600 hover:text-gray-900"
                        }`
                      }
                    >
                      {label}
                    </Tab>
                  ))}
                </Tab.List>

                <Tab.Panels className="mt-4">
                  <Tab.Panel unmount={false}>
                    <TshirtAndPositionStep
                      mode="tshirt"
                      tshirtSize={tshirtSize}
                      setTshirtSize={setTshirtSize}
                      tshirtSizes={tshirtSizes}
                      onBack={() => setActiveTab(0)}
                      onNext={() => setActiveTab(1)}
                    />
                  </Tab.Panel>

                  <Tab.Panel unmount={false}>
                    <TshirtAndPositionStep
                      mode="position"
                      position={position}
                      setPosition={setPosition}
                      positions={positions}
                      onBack={() => setActiveTab(0)}
                      onContinue={saveAndContinueToPayment}
                      saving={saving}
                      canContinue={canContinueToPayment}
                    />
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
            </div>
          </DialogPanel>
        ) : (
          <DialogPanel className="w-full max-w-4xl bg-transparent shadow-none">
            <ModalIntelliPay
              athleteId={athleteId}
              athleteEmail={athleteEmail}
              eventId={eventId}
              origin={origin}
              onCloseAll={onClose}
              tShirtSize={tshirtSize}
              bundle={bundleChoice}
            />
          </DialogPanel>
        )}
      </div>
    </Dialog>
  );
}
